import { Mail } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, Eyebrow } from "@/components/site/motion";
import { PhotoFrame } from "@/components/site/Placeholders";
import { Icon } from "@/components/site/Icon";
import { Btn } from "@/components/site/Buttons";
import { requestLink, PARTNER_EMAIL } from "@/data/site";

const WHO = ["Associazioni", "Cooperative", "Imprese", "Organizzazioni del settore"];

const BENEFITS = [
  { icon: "Sparkles", t: "Nuove opportunità di servizio" },
  { icon: "Briefcase", t: "Supporto commerciale" },
  { icon: "RadioTower", t: "Coordinamento tramite la Centrale Operativa" },
  { icon: "Network", t: "Una rete strutturata" },
  { icon: "Expand", t: "Ampliamento del bacino di attività" },
  { icon: "BadgeEuro", t: "Nessuna quota fissa di ingresso" },
  { icon: "Handshake", t: "Collaborazione basata sulle attività effettivamente svolte" },
];

export default function DiventaPartner() {
  return (
    <div data-testid="partner-page">
      <PageHero
        eyebrow="Diventa partner"
        lines={["Le competenze di ciascuno.", "La forza di una rete."]}
        intro="Stiamo ampliando la rete nazionale Nexus. Cerchiamo associazioni, cooperative, imprese e organizzazioni del settore che condividano il nostro modo di lavorare: professionalità, affidabilità e attenzione alle persone."
      >
        <div className="mt-10 flex flex-col gap-4 sm:flex-row">
          <Btn to={requestLink("partner")} testId="partner-hero-cta-btn">Entra nella rete Nexus</Btn>
          <Btn href={`mailto:${PARTNER_EMAIL}`} variant="ghost" testId="partner-hero-email-link">{PARTNER_EMAIL}</Btn>
        </div>
      </PageHero>

      <section data-testid="partner-who" className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal><Eyebrow>A chi ci rivolgiamo</Eyebrow></Reveal>
            <Reveal delay={0.1}><h2 className="mt-6 text-3xl font-semibold leading-tight text-navy md:text-[2.6rem]">Una rete costruita sulla qualità dei partner.</h2></Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base leading-relaxed text-slate2 md:text-lg">
                I partner Nexus svolgono i servizi sul territorio. La Centrale Operativa riceve le richieste, le pianifica e le affida al partner più adatto, coordinando le attività in modo chiaro e trasparente.
              </p>
            </Reveal>
            <div className="mt-10 flex flex-wrap gap-3">
              {WHO.map((w, i) => (
                <Reveal key={w} delay={i * 0.08} className="rounded-full border border-navy/15 bg-mist px-5 py-2.5 text-sm font-semibold text-navy">{w}</Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.1}>
            <PhotoFrame src="/media/terzo-settore.jpg" label="Terzo settore: la rete dei partner Nexus" className="min-h-[320px] rounded-[28px] md:min-h-[460px]" testId="partner-photo" />
          </Reveal>
        </div>
      </section>

      <section data-testid="partner-benefits" className="bg-mist py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><Eyebrow>I vantaggi</Eyebrow></Reveal>
          <Reveal delay={0.1}><h2 className="mt-6 max-w-3xl text-3xl font-semibold leading-tight text-navy md:text-[2.6rem]">Perché entrare nella rete Nexus.</h2></Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((b, i) => (
              <Reveal
                key={b.t}
                delay={(i % 4) * 0.08}
                className={`group flex flex-col justify-between rounded-[22px] border p-7 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(24,49,79,0.10)] ${i === 6 ? "border-navy bg-navy text-white sm:col-span-2" : "border-navy/10 bg-white"}`}
              >
                <Icon name={b.icon} className={`h-7 w-7 ${i === 6 ? "text-gold" : "text-gold-ink"}`} />
                <p className={`mt-10 text-lg font-semibold leading-snug ${i === 6 ? "text-white" : "text-navy"}`}>{b.t}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="partner-cta" className="grain relative overflow-hidden bg-navy py-20 text-white md:py-28">
        <div className="pointer-events-none absolute -left-32 -bottom-32 h-[460px] w-[460px] rounded-full border border-gold/25" />
        <div className="relative mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal><blockquote className="text-3xl font-light leading-snug md:text-5xl">“Le competenze di ciascuno. <span className="text-gold">La forza di una rete.</span>”</blockquote></Reveal>
          <Reveal delay={0.15} className="mt-12 flex flex-col gap-6 sm:flex-row sm:items-center">
            <Btn to={requestLink("partner")} variant="gold" testId="partner-cta-btn">Entra nella rete Nexus</Btn>
            <a href={`mailto:${PARTNER_EMAIL}`} data-testid="partner-cta-email" className="inline-flex items-center gap-3 text-white/90 hover:text-gold">
              <Mail className="h-4 w-4 text-gold" strokeWidth={1.5} /> {PARTNER_EMAIL}
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
